import { useState, useEffect, useCallback, useRef } from 'react';
import { m, AnimatePresence } from 'framer-motion';
// @mui
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  IconButton,
  Stack,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Avatar,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Modal,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
// components
import Iconify from './Iconify';
// config
import { CPO_API } from '../config';
// chatbot data
import chatbotData from '../assets/files/Chatbot.json';
// services
import { CausesAPI } from '../services/causes.services';

// ----------------------------------------------------------------------

const STORAGE_KEY = 'suggestion_submitted';

// Check if production
const isProd = window.location.hostname === 'charityphilippines.org' || 
               window.location.hostname === 'www.charityphilippines.org';

const EMAIL_TO = isProd ? ['cpowebdeveloper@gmail.com','contact@charityphilippines.org'] : ['bryanlagan@gmail.com','contact@charityphilippines.org'];

// Styled animated button container
const AnimatedButtonContainer = styled(m.div)(({ theme }) => ({
  position: 'fixed',
  bottom: 44,
  right: 44,
  zIndex: 1000,
}));

// Main support button
const MainSupportButton = styled(m.div)(({ theme }) => ({
  width: 69,
  height: 69,
  borderRadius: '50%',
  background: `linear-gradient(135deg, #2196F3 0%, #1976D2 100%)`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  boxShadow: theme.shadows[6],
  '&:hover': {
    transform: 'scale(1.1)',
  },
}));

// Option buttons container
const OptionsContainer = styled(m.div)(({ theme }) => ({
  position: 'absolute',
  bottom: 85,
  right: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

// Option button
const OptionButton = styled(m.div)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(3),
  background: theme.palette.common.white,
  border: `1px solid ${theme.palette.grey[200]}`,
  boxShadow: theme.shadows[4],
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'background-color 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
  '&:hover': {
    background: '#F3F8FF',
    borderColor: '#BFD9FF',
    boxShadow: theme.shadows[8],
  },
}));

// Styled form container for suggestions
const FormContainer = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: 24,
  right: 24,
  zIndex: 1001,
  width: 400,
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[10],
}));

// Chatbot container
const ChatbotContainer = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: 24,
  right: 24,
  zIndex: 1001,
  width: 380,
  height: 550,
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[10],
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}));

// Chatbot header
const ChatHeader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, #2196F3 0%, #1976D2 100%)`,
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  color: theme.palette.common.white,
}));

// Chat messages container
const ChatMessages = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  padding: theme.spacing(2),
  background: theme.palette.grey[50],
}));

// Message bubble
const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isBot',
})(({ theme, isBot }) => ({
  maxWidth: '80%',
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(1),
  background: isBot ? theme.palette.common.white : theme.palette.primary.main,
  color: isBot ? theme.palette.text.primary : theme.palette.common.white,
  alignSelf: isBot ? 'flex-start' : 'flex-end',
  boxShadow: theme.shadows[1],
  wordBreak: 'break-word',
}));

// Quick reply button
const QuickReplyButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  textTransform: 'none',
  fontSize: '0.85rem',
  padding: theme.spacing(0.75, 2),
  whiteSpace: 'nowrap',
}));

// Tutorial quick reply button (orange outline)
const TutorialQuickReplyButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  textTransform: 'none',
  fontSize: '0.85rem',
  padding: theme.spacing(0.75, 2),
  whiteSpace: 'nowrap',
  borderColor: '#FF8C00',
  color: '#FF8C00',
  '&:hover': {
    borderColor: '#FF6B00',
    backgroundColor: 'rgba(255, 140, 0, 0.08)',
  },
}));

// PayPal quick reply button (solid blue)
const PayPalQuickReplyButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  textTransform: 'none',
  fontSize: '0.85rem',
  padding: theme.spacing(0.75, 2),
  whiteSpace: 'nowrap',
  backgroundColor: '#0070ba',
  color: 'white',
  border: 'none',
  '&:hover': {
    backgroundColor: '#005ea6',
  },
}));

// Chat input container
const ChatInputContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  background: theme.palette.common.white,
}));

// Animated character for suggestion success
const AnimatedCharacter = styled(m.div)(({ theme }) => ({
  width: 60,
  height: 60,
  borderRadius: '50%',
  background: `linear-gradient(135deg, #FF8C00 0%, #FF6B00 100%)`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  boxShadow: theme.shadows[6],
}));

// Speech bubble for success message
const SpeechBubble = styled(m.div)(({ theme }) => ({
  position: 'absolute',
  bottom: 75,
  right: 0,
  background: theme.palette.common.white,
  padding: theme.spacing(2, 3),
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[8],
  width: 282,
  border: `2px solid #FF8C00`,
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -10,
    right: 25,
    width: 0,
    height: 0,
    borderLeft: '10px solid transparent',
    borderRight: '10px solid transparent',
    borderTop: `10px solid #FF8C00`,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: -8,
    right: 26,
    width: 0,
    height: 0,
    borderLeft: '8px solid transparent',
    borderRight: '8px solid transparent',
    borderTop: `8px solid ${theme.palette.common.white}`,
  },
}));

// ----------------------------------------------------------------------

export default function SupportButton() {
  const [isVisible, setIsVisible] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [activePanel, setActivePanel] = useState(null); // 'chatbot' | 'suggestion' | null
  const [opacity, setOpacity] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  
  // Suggestion form states
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  
  // Chatbot states
  const [messages, setMessages] = useState([]);
  const [currentFlow, setCurrentFlow] = useState(null);
  const [userInput, setUserInput] = useState('');
  const [sessionData, setSessionData] = useState({});
  const [isTyping, setIsTyping] = useState(false);
  const [causesData, setCausesData] = useState([]);
  const [waitingForInput, setWaitingForInput] = useState(false);
  const [expectedInputType, setExpectedInputType] = useState(null);
  const [endCountdown, setEndCountdown] = useState(null);
  const countdownTimeoutRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Tutorial video modal states
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoModalTitle, setVideoModalTitle] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [pendingTutorial, setPendingTutorial] = useState(null);
  
  // Random popup message states
  const [showRandomMessage, setShowRandomMessage] = useState(false);
  const [currentMessage, setCurrentMessage] = useState('');

  // Tutorial video URLs (dummy for now)
  const tutorialVideos = {
    TUTORIAL_DONATE: {
      title: 'How to Donate',
      url: 'https://charity-project-images.s3.ap-southeast-1.amazonaws.com/How+to+donate+to+multiple+projects.mov'
    },
    TUTORIAL_VOUCHER: {
      title: 'How to Donate via QRPh',
      url: 'https://charity-project-images.s3.ap-southeast-1.amazonaws.com/How+to+donate+via+QRPh.mp4'
    },
    TUTORIAL_GIFTCARD: {
      title: 'How to Use Gift Card',
      url: 'https://charity-project-images.s3.ap-southeast-1.amazonaws.com/How+to+use+Gift+Card+icon.mov'
    }
  };

  const isDirectVideoFile = (url) => /\.(mp4|mov|webm|ogg)(\?.*)?$/i.test(url || '');

  // Random popup messages
  const randomMessages = [
    'How may I help you?',
    'Need assistance with donations?',
    'Got questions? Let\'s chat!',
    'I\'m here to help!',
    'Explore our projects together?'
  ];

  const hasSubmittedSuggestion = localStorage.getItem(STORAGE_KEY) === 'true';

  // Scroll to bottom of chat
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Random popup message timer
  useEffect(() => {
    if (!isVisible || activePanel || showSuccessMessage || showOptions) return;
    
    const showRandomPopup = () => {
      if (!showRandomMessage && !activePanel && !showSuccessMessage) {
        const randomIndex = Math.floor(Math.random() * randomMessages.length);
        setCurrentMessage(randomMessages[randomIndex]);
        setShowRandomMessage(true);
        
        // Hide message after 3 seconds
        setTimeout(() => {
          setShowRandomMessage(false);
        }, 3000);
      }
    };

    // Show first popup after 5 seconds, then every 15-25 seconds
    const initialTimer = setTimeout(showRandomPopup, 5000);
    
    const interval = setInterval(() => {
      showRandomPopup();
    }, Math.random() * 10000 + 15000); // Random interval between 15-25 seconds

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, [isVisible, activePanel, showSuccessMessage, showOptions, showRandomMessage, randomMessages]);

  // Handle scroll to detect position
  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = document.documentElement.clientHeight;
    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
    
    if (scrollTop < 100) {
      setIsVisible(false);
      setOpacity(0);
      return;
    }
    
    setIsVisible(true);
    
    if (distanceFromBottom <= 10) {
      setIsAtBottom(true);
      setOpacity(1);
    } else if (distanceFromBottom <= 400) {
      setIsAtBottom(false);
      const fadeOpacity = 1 - (distanceFromBottom / 400);
      setOpacity(Math.max(0.3, fadeOpacity));
    } else {
      setIsAtBottom(false);
      setOpacity(0.3);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Initialize chatbot with greeting
  const initializeChatbot = () => {
    const greeting = chatbotData.intents.find(i => i.id === 'greeting');
    if (greeting) {
      addBotMessage(greeting.response.text, greeting.response.quick_replies);
    }
  };

  // Add bot message
  const addBotMessage = (text, quickReplies = null, delay = 500) => {
    setIsTyping(true);
    setTimeout(() => {
      setMessages(prev => [...prev, { 
        type: 'bot', 
        text: replaceVariables(text),
        quickReplies 
      }]);
      setIsTyping(false);
    }, delay);
  };

  // Add user message
  const addUserMessage = (text) => {
    setMessages(prev => [...prev, { type: 'user', text }]);
  };
  // Fetch causes from API
  const fetchCauses = async () => {
    try {
      console.log('Fetching causes from API...'); // Debug log
      const response = await CausesAPI.getAllCauses();
      console.log('API Response:', response); // Debug log
      
      if (response && response.data && response.data.records) {
        const data = response.data.records;
        // Filter out 'Others' and sort causes
        const filteredCauses = data.filter((c) => c.name !== 'Others');
        console.log('Filtered causes:', filteredCauses); // Debug log
        setCausesData(filteredCauses);
        return filteredCauses;
      }
      
      console.log('Unexpected API response structure:', response);
      return [];
    } catch (error) {
      console.error('Error fetching causes:', error);
      return [];
    }
  };
  // Send email for support requests
  const sendSupportEmail = async (topic, message, email) => {
    try {
      const subject = isProd 
        ? `${topic} - CPO Chatbot Support` 
        : `Test ${topic} - CPO Chatbot Support`;
      
      await fetch(`${CPO_API}/email/mailer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'chatbot - support',
          emailFrom: email,
          subject,
          content: message,
          emailTo: EMAIL_TO,
        }),
      });
      addBotMessage('✅ Your support request has been sent. Our team will get back to you soon.', null, 200);
      console.log('Support email sent successfully');
    } catch (error) {
      console.error('Error sending support email:', error);
      addBotMessage('❌ There was a problem sending your support request. Please try again later.', null, 200);
    }
  };

  // Replace template variables
  const replaceVariables = (text) => {
    if (!text) return text;
    
    let result = text;
    
    // Replace bot defaults
    Object.entries(chatbotData.bot.defaults).forEach(([key, value]) => {
      result = result.replace(new RegExp(`{{bot\\.defaults\\.${key}}}`, 'g'), value);
    });
    
    // Replace session data
    Object.entries(sessionData).forEach(([key, value]) => {
      result = result.replace(new RegExp(`{{${key}}}`, 'g'), value);
    });
    
    return result;
  };

  // Handle flow navigation
  const handleFlowNavigation = async (flowId, newData = {}) => {
    const updatedSessionData = { ...sessionData, ...newData };
    setSessionData(updatedSessionData);
    
    if (flowId === 'EXPLORE_BY_CAUSE') {
      // Fetch causes from API and create dynamic quick replies
      setIsTyping(true);
      const causes = await fetchCauses();
      setIsTyping(false);
      
      console.log('Fetched causes:', causes); // Debug log
      
      if (causes.length > 0) {
        const dynamicQuickReplies = causes.map(cause => ({
          title: cause.name,
          set: { cause: cause.name, causeId: cause._id },
          go: 'CAUSE_SELECTED'
        }));
        
        console.log('Dynamic quick replies:', dynamicQuickReplies); // Debug log
        addBotMessage('Pick a cause:', dynamicQuickReplies);
      } else {
        // Fallback to original flow if API fails
        const flow = chatbotData.flows[flowId];
        if (flow) {
          addBotMessage(flow.message.text, flow.message.quick_replies);
        }
      }
      setCurrentFlow(flowId);
      return;
    }

    if (flowId === 'EXPLORE_FEATURED') {
      // Handle featured projects API call
      setIsTyping(true);
      try {
        const response = await fetch('https://dev-api.charityphilippines.org/project/feature');
        const data = await response.json();
        
        if (data.success && data.data && data.data.length > 0) {
          const projects = data.data;
          
          // Create project tiles message
          const projectTiles = projects.slice(0, 6).map(project => ({
            title: project.name,
            subtitle: project.shortDescription,
            image: project.mainImage,
            buttons: [
              {
                title: 'View Project',
                type: 'url',
                url: `project-details/${project._id}`,
                target: '_blank'
              },
              // {
              //   title: 'Donate',
              //   go: 'DONATE_START',
              //   set: { projectId: project._id }
              // }
            ]
          }));

          setIsTyping(false);
          addBotMessage('Here are some featured projects. Tap one to view details or donate.');
          
          // Add project tiles to messages
          setMessages(prev => [...prev, {
            type: 'projects',
            projects: projectTiles,
            timestamp: new Date().toISOString()
          }]);
        } else {
          setIsTyping(false);
          addBotMessage('Sorry, I couldn\'t load the featured projects right now. Please try again later.');
        }
      } catch (error) {
        console.error('Error fetching featured projects:', error);
        setIsTyping(false);
        addBotMessage('Sorry, I couldn\'t load the featured projects right now. Please try again later.');
      }
      setCurrentFlow(flowId);
      return;
    }
    
    if (flowId === 'CAUSE_SELECTED') {
      // Handle cause selection - show confirmation instead of immediate redirect
      const selectedCause = updatedSessionData.cause;
      console.log('Selected cause for confirmation:', selectedCause); // Debug log
    }

    if (flowId === 'CAUSE_REDIRECT_CONFIRM') {
      // Handle confirmed redirection
      const selectedCause = sessionData.cause;
      console.log('Confirmed redirect for cause:', selectedCause); // Debug log
      addBotMessage('Taking you to the explore page now...');
      
      // Redirect after a short delay
      setTimeout(() => {
        const encodedCause = encodeURIComponent(selectedCause);
        const redirectUrl = `/explore-project?cause=${encodedCause}`;
        console.log('Redirecting to:', redirectUrl); // Debug log
        window.location.href = redirectUrl;
      }, 1500);
      return;
    }

    if (flowId === 'KEYWORD_SELECTED') {
      // Handle keyword selection - show confirmation instead of immediate redirect
      const selectedKeyword = updatedSessionData.keyword;
      console.log('Selected keyword for confirmation:', selectedKeyword); // Debug log
    }

    if (flowId === 'KEYWORD_REDIRECT_CONFIRM') {
      // Handle confirmed keyword redirection
      const selectedKeyword = sessionData.keyword;
      console.log('Confirmed redirect for keyword:', selectedKeyword); // Debug log
      addBotMessage('Taking you to the search results now...');
      
      // Redirect after a short delay
      setTimeout(() => {
        const encodedKeyword = encodeURIComponent(selectedKeyword);
        const redirectUrl = `/explore-project?keyword=${encodedKeyword}`;
        console.log('Redirecting to:', redirectUrl); // Debug log
        window.location.href = redirectUrl;
      }, 1500);
      return;
    }

    // Handle contact support email sending
    if (flowId === 'CONTACT_SUPPORT_SEND') {
      const { supportMessage, contactEmail } = updatedSessionData;
      await sendSupportEmail('Contact Support', supportMessage, contactEmail);
    }

    // Handle corporate contact email sending  
    if (flowId === 'CONTACT_CORPORATE_SEND') {
      const { corporateEmail } = updatedSessionData;
      await sendSupportEmail('Corporate Inquiry', 'Corporate partnership inquiry', corporateEmail);
    }

    // Handle receipt help email sending
    if (flowId === 'SUPPORT_RECEIPT_CONFIRM') {
      const { receiptEmail, receiptDetails } = updatedSessionData;
      const message = `Receipt help request for: ${receiptEmail}\nDetails: ${receiptDetails}`;
      await sendSupportEmail('Receipt Help', message, receiptEmail);
    }

    // Handle payment issue email sending
    if (flowId === 'SUPPORT_PAYMENT_SEND') {
      const { supportMessage } = updatedSessionData;
      // Extract email from the support message for the "from" field
      const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/;
      const emailMatch = supportMessage.match(emailRegex);
      const userEmail = emailMatch ? emailMatch[0] : 'unknown@example.com';
      
      await sendSupportEmail('Payment Issue', supportMessage, userEmail);
    }

    // Handle support confirm email sending (for reports and corporate)
    if (flowId === 'SUPPORT_CONFIRM') {
      const { supportMessage } = updatedSessionData;
      // Try to extract email from message, otherwise use a default
      const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/;
      const emailMatch = supportMessage.match(emailRegex);
      const userEmail = emailMatch ? emailMatch[0] : 'noreply@charityphilippines.org';
      
      await sendSupportEmail('Support Request', supportMessage, userEmail);
    }
    
    const flow = chatbotData.flows[flowId];
    if (flow) {
      setCurrentFlow(flowId);
      
      // Check if this flow expects user input
      if (flow.message.expects) {
        setWaitingForInput(true);
        setExpectedInputType(flow.message.expects.type);
        addBotMessage(flow.message.text);
      } else {
        setWaitingForInput(false);
        setExpectedInputType(null);
        addBotMessage(flow.message.text, flow.message.quick_replies);
      }
    }
  };

  // Handle tutorial confirmation in chat
  const handleTutorialConfirm = () => {
    if (pendingTutorial && tutorialVideos[pendingTutorial]) {
      const tutorial = tutorialVideos[pendingTutorial];
      setVideoModalTitle(tutorial.title);
      setVideoUrl(tutorial.url);
      setPendingTutorial(null);
      setShowVideoModal(true);
    }
  };

  const handleTutorialCancel = () => {
    setPendingTutorial(null);
    // Redirect back to main greeting with all options
    const greeting = chatbotData.intents.find(i => i.id === 'greeting');
    if (greeting) {
      addBotMessage('No problem! What would you like to do?', greeting.response.quick_replies);
    }
  };

  // Handle quick reply click
  const handleQuickReply = (reply) => {
    addUserMessage(reply.title);

    // Handle tutorial confirmation quick replies FIRST (before general tutorial check)
    if (reply.go === 'TUTORIAL_CONFIRM') {
      handleTutorialConfirm();
      return;
    }
    if (reply.go === 'TUTORIAL_CANCEL') {
      handleTutorialCancel();
      return;
    }

    // Check if this is a URL redirect
    if (reply.type === 'url' && reply.url) {
      window.open(reply.url, '_blank');
      return;
    }

    // Check if this is a tutorial flow (TUTORIAL_DONATE, TUTORIAL_VOUCHER, TUTORIAL_GIFTCARD)
    if (reply.go && reply.go.startsWith('TUTORIAL_')) {
      // Prevent multiple confirmation prompts
      if (pendingTutorial) return;
      setPendingTutorial(reply.go);
      // Insert confirmation message as a bot message with Cancel/Okay buttons
      addBotMessage(
        'I am going to show the tutorial video. Would you like to proceed?',
        [
          { title: 'Cancel', go: 'TUTORIAL_CANCEL', style: 'tutorial-cancel' },
          { title: 'Okay', go: 'TUTORIAL_CONFIRM', style: 'tutorial-confirm' }
        ],
        200
      );
      return;
    }

    // Navigate to next flow with any set data
    if (reply.go) {
      setTimeout(() => handleFlowNavigation(reply.go, reply.set || {}), 300);
    }
  };

  // Handle user text input
  const handleUserInput = (e) => {
    e.preventDefault();
    if (!userInput.trim()) return;
    
    const text = userInput.trim();
    addUserMessage(text);
    setUserInput('');
    
    // If we're waiting for specific input from a flow
    if (waitingForInput && currentFlow) {
      const flow = chatbotData.flows[currentFlow];
      if (flow && flow.message.expects) {
        const { saveAs, go, type } = flow.message.expects;
        
        // Basic validation for email type
        if (type === 'email' && !text.includes('@')) {
          addBotMessage('Please provide a valid email address.');
          return;
        }
        
        // Validation for payment issue text
        if (type === 'validated_text' && currentFlow === 'SUPPORT_PAYMENT') {
          // Check minimum length
          if (text.length < 30) {
            addBotMessage('Please provide more details about your payment issue (at least 30 characters).');
            return;
          }
          
          // Check if text contains an email
          const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/;
          if (!emailRegex.test(text)) {
            addBotMessage('Please include the email address you used for the donation in your message.');
            return;
          }
        }
        
        // Validation for support contact text
        if (type === 'free_text' && currentFlow === 'CONTACT_SUPPORT_START') {
          // Check minimum length
          if (text.length < 20) {
            addBotMessage('Please provide more details about your issue (at least 20 characters).');
            return;
          }
          
          // Check maximum length
          if (text.length > 150) {
            addBotMessage('Please keep your message under 150 characters.');
            return;
          }
        }
        
        // Save the input and proceed to next flow
        const newData = { [saveAs]: text };
        setSessionData(prev => ({ ...prev, ...newData }));
        setWaitingForInput(false);
        setExpectedInputType(null);
        
        if (go) {
          setTimeout(() => handleFlowNavigation(go, newData), 300);
        }
        return;
      }
    }
    
    // Check for intent matches when not waiting for specific input
    const matchedIntent = chatbotData.intents.find(intent => 
      intent.trainingPhrases.some(phrase => 
        text.toLowerCase().includes(phrase.toLowerCase())
      )
    );
    
    if (matchedIntent) {
      setWaitingForInput(false);
      setExpectedInputType(null);
      addBotMessage(matchedIntent.response.text, matchedIntent.response.quick_replies);
    } else {
      // Use fallback
      const fallback = chatbotData.intents.find(i => i.id === 'fallback');
      if (fallback) {
        setWaitingForInput(false);
        setExpectedInputType(null);
        addBotMessage(fallback.response.text, fallback.response.quick_replies);
      }
    }
  };

  // Main button click handler
  const handleMainButtonClick = () => {
    if (activePanel) {
      // If a panel is open, close it
      setActivePanel(null);
      setShowOptions(false);
    } else {
      // Toggle options menu
      setShowOptions(!showOptions);
    }
  };

  // Open chatbot
  const handleOpenChatbot = () => {
    setShowOptions(false);
    setActivePanel('chatbot');
    if (messages.length === 0) {
      initializeChatbot();
    }
  };

  // Open suggestion form
  const handleOpenSuggestion = () => {
    setShowOptions(false);
    setActivePanel('suggestion');
  };

  // Close panels
  const handleClose = () => {
    setActivePanel(null);
    setShowOptions(false);
    setEmail('');
    setMessage('');
  };

  // Reset chatbot
  const handleResetChatbot = () => {
    setMessages([]);
    setSessionData({});
    setCurrentFlow(null);
    setWaitingForInput(false);
    setExpectedInputType(null);
    initializeChatbot();
  };

  const returnToChatMainMenu = () => {
    setMessages([]);
    setSessionData({});
    setCurrentFlow(null);
    setWaitingForInput(false);
    setExpectedInputType(null);
    setEndCountdown(null);
    initializeChatbot();
  };

  const lastMessage = messages[messages.length - 1];
  const isAtConversationEnd =
    activePanel === 'chatbot' &&
    !isTyping &&
    !waitingForInput &&
    messages.length > 0 &&
    lastMessage?.type === 'bot' &&
    !lastMessage?.quickReplies;

  // Auto-return to chat main menu 10 seconds after reaching a terminal response.
  useEffect(() => {
    if (countdownTimeoutRef.current) {
      clearTimeout(countdownTimeoutRef.current);
      countdownTimeoutRef.current = null;
    }

    if (!isAtConversationEnd) {
      if (endCountdown !== null) setEndCountdown(null);
      return undefined;
    }

    if (endCountdown === null) {
      setEndCountdown(10);
      return undefined;
    }

    if (endCountdown <= 0) {
      returnToChatMainMenu();
      return undefined;
    }

    countdownTimeoutRef.current = setTimeout(() => {
      setEndCountdown((prev) => (prev === null ? null : prev - 1));
    }, 1000);

    return () => {
      if (countdownTimeoutRef.current) {
        clearTimeout(countdownTimeoutRef.current);
      }
    };
  }, [isAtConversationEnd, endCountdown, activePanel]);

  // Submit suggestion
  const handleSubmitSuggestion = async (e) => {
    e.preventDefault();
    
    if (!email || !message) {
      setSnackbar({ open: true, message: 'Please fill in all fields', severity: 'error' });
      return;
    }

    setIsSubmitting(true);

    const emailType = isProd ? "Donor's Suggestion" : 'Test Suggestion';

    try {
      const response = await fetch(`${CPO_API}/email/mailer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'suggestion',
          emailFrom: email,
          subject: emailType,
          content: message,
          emailTo: EMAIL_TO,
        }),
      });

      if (response.ok) {
        localStorage.setItem(STORAGE_KEY, 'true');
        setActivePanel(null);
        setShowSuccessMessage(true);
        setEmail('');
        setMessage('');
        
        setTimeout(() => {
          setShowSuccessMessage(false);
        }, 5000);
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      console.error('Error submitting suggestion:', error);
      setSnackbar({ open: true, message: 'Failed to submit. Please try again.', severity: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <AnimatePresence>
        {/* Main Support Button */}
        {!activePanel && !showSuccessMessage && (
          <AnimatedButtonContainer
            initial={{ opacity: 0, scale: 0, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0, y: 50 }}
            transition={{ 
              opacity: { duration: 0.3 },
              scale: { type: 'spring', stiffness: 260, damping: 20 },
              y: { type: 'spring', stiffness: 260, damping: 20 }
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {/* Options Menu */}
            <AnimatePresence>
              {showOptions && (
                <OptionsContainer
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.2 }}
                  mbox={isHovered ? 10 : 0}
                >
                  <OptionButton
                    whileHover={{ scale: 1.05, x: -5 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleOpenChatbot}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Iconify icon="mdi:robot-happy-outline" sx={{ color: 'white', width: 22, height: 22 }} />
                    </Box>
                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: '18px' }}>
                      Chat with us
                    </Typography>
                  </OptionButton>

                  {/* {!hasSubmittedSuggestion && ( */}
                    <OptionButton
                      whileHover={{ scale: 1.05, x: -5 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleOpenSuggestion}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #FF8C00 0%, #FF6B00 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Iconify icon="mdi:lightbulb-on-outline" sx={{ color: 'white', width: 22, height: 22 }} />
                      </Box>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: '18px' }}>
                        Give suggestions
                      </Typography>
                    </OptionButton>
                  {/* )} */}
                </OptionsContainer>
              )}
            </AnimatePresence>

            {/* Main Button */}
            <MainSupportButton
              onClick={handleMainButtonClick}
              animate={{
                y: showOptions ? 0 : [0, -5, 0],
                rotate: showOptions ? 45 : 0,
              }}
              transition={{
                y: { duration: 2, repeat: showOptions ? 0 : Infinity, repeatType: 'loop', ease: 'easeInOut' },
                rotate: { duration: 0.2 }
              }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <Iconify 
                icon={showOptions ? "mdi:close" : "mdi:headset"} 
                sx={{ 
                  width: 32, 
                  height: 32, 
                  color: 'common.white',
                }} 
              />
            </MainSupportButton>
          </AnimatedButtonContainer>
        )}
      </AnimatePresence>

      {/* Chatbot Panel */}
      <AnimatePresence>
        {activePanel === 'chatbot' && (
          <ChatbotContainer
            component={m.div}
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 50 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          >
            {/* Header */}
            <ChatHeader>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 40, height: 40 }}>
                  <Iconify icon="mdi:robot-happy-outline" sx={{ width: 24, height: 24 }} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {chatbotData.bot.name}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Online • Ready to help
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={0.5}>
                <IconButton size="small" onClick={handleResetChatbot} sx={{ color: 'white' }}>
                  <Iconify icon="mdi:refresh" sx={{ width: 20, height: 20 }} />
                </IconButton>
                <IconButton size="small" onClick={handleClose} sx={{ color: 'white' }}>
                  <Iconify icon="mdi:close" sx={{ width: 20, height: 20 }} />
                </IconButton>
              </Stack>
            </ChatHeader>

            {/* Messages */}
            <ChatMessages>
              <Stack spacing={1}>
                {messages.map((msg, index) => (
                  <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.type === 'bot' ? 'flex-start' : 'flex-end' }}>
                    {msg.type === 'projects' ? (
                      // Project tiles display
                      <Box sx={{ width: '100%' }}>
                        <Stack spacing={1}>
                          {msg.projects.map((project, pIndex) => (
                            <Box
                              key={pIndex}
                              sx={{
                                border: '1px solid #e0e0e0',
                                borderRadius: 2,
                                overflow: 'hidden',
                                bgcolor: 'white',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              }}
                            >
                              {project.image && (
                                <Box
                                  component="img"
                                  src={project.image}
                                  alt={project.title}
                                  sx={{
                                    width: '100%',
                                    height: 120,
                                    objectFit: 'cover',
                                  }}
                                />
                              )}
                              <Box sx={{ p: 2 }}>
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                  {project.title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                                  {project.subtitle?.length > 80 
                                    ? `${project.subtitle.substring(0, 80)}...` 
                                    : project.subtitle}
                                </Typography>
                                <Stack direction="row" spacing={1}>
                                  {project.buttons.map((button, bIndex) => (
                                    <Button
                                      key={bIndex}
                                      size="small"
                                      variant={button.title === 'View Project' ? 'outlined' : 'contained'}
                                      color="primary"
                                      onClick={() => {
                                        if (button.type === 'url') {
                                          window.open(button.url, button.target || '_self');
                                        } else if (button.go) {
                                          handleQuickReply({
                                            title: button.title,
                                            go: button.go,
                                            set: button.set
                                          });
                                        }
                                      }}
                                      sx={{ fontSize: '0.75rem' }}
                                    >
                                      {button.title}
                                    </Button>
                                  ))}
                                </Stack>
                              </Box>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    ) : (
                      <MessageBubble isBot={msg.type === 'bot'}>
                        <Typography variant="body2">{msg.text}</Typography>
                      </MessageBubble>
                    )}
                    
                    {/* Quick Replies */}
                    {msg.type === 'bot' && msg.quickReplies && index === messages.length - 1 && (
                      <Stack 
                        direction="row" 
                        flexWrap="wrap" 
                        gap={1} 
                        sx={{ mt: 1, maxWidth: '100%' }}
                      >
                        {msg.quickReplies.map((reply, rIndex) => {
                          if (reply.style === 'tutorial') {
                            return (
                              <TutorialQuickReplyButton
                                key={rIndex}
                                variant="outlined"
                                size="small"
                                onClick={() => handleQuickReply(reply)}
                              >
                                {reply.title}
                              </TutorialQuickReplyButton>
                            );
                          }
                          
                          if (reply.style === 'paypal') {
                            return (
                              <PayPalQuickReplyButton
                                key={rIndex}
                                variant="contained"
                                size="small"
                                onClick={() => handleQuickReply(reply)}
                                startIcon={reply.icon && <Iconify icon={reply.icon} />}
                              >
                                {reply.title}
                              </PayPalQuickReplyButton>
                            );
                          }
                          
                          return (
                            <QuickReplyButton
                              key={rIndex}
                              variant="outlined"
                              size="small"
                              onClick={() => handleQuickReply(reply)}
                            >
                              {reply.title}
                            </QuickReplyButton>
                          );
                        })}
                      </Stack>
                    )}
                  </Box>
                ))}
                
                {/* Typing indicator */}
                {isTyping && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MessageBubble isBot>
                      <Stack direction="row" spacing={0.5}>
                        {[0, 1, 2].map((i) => (
                          <Box
                            key={i}
                            component={m.div}
                            animate={{ y: [0, -5, 0] }}
                            transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.2 }}
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: 'grey.400',
                            }}
                          />
                        ))}
                      </Stack>
                    </MessageBubble>
                  </Box>
                )}
                
                <div ref={messagesEndRef} />
              </Stack>
            </ChatMessages>

            {isAtConversationEnd && endCountdown !== null && (
              <ChatInputContainer>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Typography variant="caption" color="text.secondary">
                    Returning to main menu in {endCountdown}s
                  </Typography>
                  <Button size="small" variant="outlined" onClick={returnToChatMainMenu}>
                    Back to Menu
                  </Button>
                </Stack>
              </ChatInputContainer>
            )}

            {/* Input */}
            {waitingForInput && (
              <ChatInputContainer>
                <form onSubmit={handleUserInput}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={
                      expectedInputType === 'email' 
                        ? 'Enter your email address...' 
                        : 'Type your response...'
                    }
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton type="submit" size="small" color="primary">
                            <Iconify icon="mdi:send" sx={{ width: 20, height: 20 }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                      sx: { 
                        borderRadius: 3,
                        fontSize: '16px',
                      },
                    }}
                  />
                </form>
              </ChatInputContainer>
            )}
          </ChatbotContainer>
        )}
      </AnimatePresence>

      {/* Suggestion Form */}
      <AnimatePresence>
        {activePanel === 'suggestion' && (
          <FormContainer
            component={m.div}
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 50 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                💡 Share Your Suggestion
              </Typography>
              <IconButton size="small" onClick={handleClose}>
                <Iconify icon="mdi:close" />
              </IconButton>
            </Stack>

            <form onSubmit={handleSubmitSuggestion}>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Your Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isSubmitting}
                  sx={{ '& .MuiInputBase-root': { fontSize: '16px' } }}
                />
                <TextField
                  fullWidth
                  label="Your Suggestion"
                  multiline
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  disabled={isSubmitting}
                  sx={{ '& .MuiInputBase-root': { fontSize: '16px' } }}
                />
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={handleClose}
                    disabled={isSubmitting}
                    sx={{ minWidth: 80 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
                    sx={{ 
                      minWidth: 100,
                      backgroundColor: '#FF8C00',
                      '&:hover': {
                        backgroundColor: '#FF6B00',
                      },
                    }}
                  >
                    {isSubmitting ? 'Sending...' : 'Submit'}
                  </Button>
                </Stack>
              </Stack>
            </form>
          </FormContainer>
        )}
      </AnimatePresence>

      {/* Random Popup Message */}
      <AnimatePresence>
        {showRandomMessage && !activePanel && !showSuccessMessage && (
          <AnimatedButtonContainer
            initial={{ opacity: 0, scale: 0, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0, y: 50 }}
            transition={{ 
              opacity: { duration: 0.3 },
              scale: { type: 'spring', stiffness: 260, damping: 20 },
              y: { type: 'spring', stiffness: 260, damping: 20 }
            }}
          >
            <SpeechBubble
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              sx={{ borderColor: '#2196F3' }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 500,
                  color: '#333',
                  textAlign: 'center',
                  lineHeight: 1.4,
                  hyphens: 'auto',
                }}
              >
                💬 {currentMessage}
              </Typography>
            </SpeechBubble>

            <MainSupportButton
              animate={{
                y: [0, -5, 0],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                repeatType: 'loop',
                ease: 'easeInOut',
              }}
              onClick={() => {
                setShowRandomMessage(false);
                handleOpenChatbot();
              }}
            >
              <Iconify 
                icon="mdi:headset" 
                sx={{ 
                  width: 32, 
                  height: 32, 
                  color: 'common.white',
                }} 
              />
            </MainSupportButton>
          </AnimatedButtonContainer>
        )}
      </AnimatePresence>

      {/* Success Message Bubble */}
      <AnimatePresence>
        {showSuccessMessage && (
          <AnimatedButtonContainer
            initial={{ opacity: 0, scale: 0, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0, y: 50 }}
            transition={{ 
              opacity: { duration: 0.3 },
              scale: { type: 'spring', stiffness: 260, damping: 20 },
              y: { type: 'spring', stiffness: 260, damping: 20 }
            }}
          >
            <SpeechBubble
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              sx={{ borderColor: '#4CAF50' }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 500,
                  color: '#333',
                  textAlign: 'center',
                  lineHeight: 1.4,
                  hyphens: 'auto',
                }}
              >
                ✅ Thank you for your suggestion! We appreciate your feedback.
              </Typography>
            </SpeechBubble>

            <AnimatedCharacter
              animate={{
                y: [0, -8, 0],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                repeatType: 'loop',
                ease: 'easeInOut',
              }}
              sx={{ background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)' }}
            >
              <Iconify 
                icon="mdi:check-bold" 
                sx={{ 
                  width: 32, 
                  height: 32, 
                  color: 'common.white',
                }} 
              />
            </AnimatedCharacter>
          </AnimatedButtonContainer>
        )}
      </AnimatePresence>



      {/* Video Modal */}
      <Modal
        open={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        aria-labelledby="video-modal-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '75vw',
            height: '75vh',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 2,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography id="video-modal-title" variant="h6" component="h2">
              {videoModalTitle}
            </Typography>
            <IconButton onClick={() => setShowVideoModal(false)} size="small">
              <Iconify icon="mdi:close" />
            </IconButton>
          </Box>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              flex: 1,
              minHeight: 0,
              bgcolor: 'grey.900',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            {isDirectVideoFile(videoUrl) ? (
              <Box
                component="video"
                controls
                autoPlay
                preload="metadata"
                src={videoUrl}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'block',
                  backgroundColor: '#000',
                }}
              >
                Your browser does not support this video format.
              </Box>
            ) : (
              <iframe
                src={videoUrl}
                title={videoModalTitle}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </Box>
          {isDirectVideoFile(videoUrl) ? (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
              If the video does not play in your browser, open it directly:{' '}
              <a href={videoUrl} target="_blank" rel="noreferrer">{videoUrl}</a>
            </Typography>
          ) : null}
        </Box>
      </Modal>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
